import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import useApi from "../../../../utils/api-manager/Helper/useApi";
import AuthContext from "../../../../utils/secure-route/AuthContext";
import Can from "../../../../utils/permissions/Can";
import { runCloverLocalSale } from "../../../../utils/clover/cloverConnector";
import "./Configuration.css";

const PROVIDER_CONFIG = {
  CLOVER: {
    name: "Clover",
    logoUrl: "https://cloverstatic.com/web/apps/cloverdotcom/release::v2.142.0-f81e9288a386b/assets/media/clover-logo.5637c88fda21055b797e300e16140c95.svg",
    connectionTypes: [
      { value: "LOCAL_LAN", label: "Same Wi-Fi as this computer (recommended)" },
      { value: "CLOUD", label: "Internet / Cloud Mode" }
    ],
    models: [
      { value: "FLEX_4", label: "Clover Flex 4" },
      { value: "CLOVER_MINI", label: "Clover Mini" },
      { value: "CLOVER_COMPACT", label: "Clover Compact" }
    ],
    defaultPort: "12345",
    steps: [
      "On the Clover machine's screen, find and open the app called \"REST Pay Display\". (Don't see it? Ask whoever set up the machine to install it for you — it's a 1-minute job.)",
      "Check that the Clover machine and this computer are on the SAME Wi-Fi — the same one your shop uses every day. If one is on mobile data or a different Wi-Fi, this won't work.",
      "On the Clover machine, go to Settings, then Network (or Wi-Fi). You'll see 4 numbers separated by dots, like 192.168.1.45 — that's the \"IP Address\". Type it exactly into the \"Machine IP Address\" box below.",
      "Still on the Clover machine, find the \"Serial Number\" — it's usually printed on a sticker on the back of the device, or under Settings > About. Type it into \"Device Serial / ID\" below.",
      "In \"POS ID Reference\" below, just type any short name for this till, like register-1 or front-counter."
    ],
    tip: "All of this information lives on the Clover machine itself — you don't need to log into any website. If you get stuck, the person who installed your Clover machine (or Clover's own support line) can read these numbers to you over the phone."
  },
  STRIPE: {
    name: "Stripe Terminal",
    logoUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/b/ba/Stripe_Logo%2C_revised_2016.svg/330px-Stripe_Logo%2C_revised_2016.svg.png?utm_source=commons.wikimedia.org&utm_campaign=index&utm_content=thumbnail",
    connectionTypes: [
      { value: "CLOUD", label: "Internet / Cloud Mode" }
    ],
    models: [
      { value: "STRIPE_S700", label: "Stripe Reader S700" },
      { value: "STRIPE_S710", label: "Stripe Reader S710" },
      { value: "WISEPOS_E", label: "BBPOS WisePOS E" }
    ],
    defaultPort: "",
    steps: [
      "On any computer's web browser, go to dashboard.stripe.com and log in with your Stripe account (the one you already use to get paid online, if you have one).",
      "On the left-hand menu, click Payments, then Terminal, then Readers.",
      "Find your card machine in the list — this is the small device your customers tap or swipe their card on. Next to it you'll see a code starting with tmr_, like tmr_1AbCdEf. Copy that.",
      "Paste that code into \"Reader / Terminal ID\" below. Then go to Developers > API Keys on the same website, copy your Secret Key, and paste it into the \"API Key / Secret Token\" box (keep this one private, like a password)."
    ],
    tip: "If this feels technical, it's fine to ask whoever manages your online payments (an accountant, IT helper, or Stripe's support chat) to send you these two codes — you're just copy-pasting them in."
  },
  SQUARE: {
    name: "Square",
    logoUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/3/3d/Square%2C_Inc._logo.svg/3840px-Square%2C_Inc._logo.svg.png?utm_source=commons.wikimedia.org&utm_campaign=index&utm_content=thumbnail",
    connectionTypes: [
      { value: "CLOUD", label: "Internet / Cloud Mode" }
    ],
    models: [
      { value: "SQUARE_TERMINAL", label: "Square Terminal" }
    ],
    defaultPort: "",
    steps: [
      "On a computer, go to developer.squareup.com and log in with your Square account.",
      "Click Terminal API, then look for a button like \"Generate Pairing Code\". It will show a short code on screen.",
      "On the physical Square Terminal device, enter that same code when it asks you to pair — the two will connect and Square will show you a \"Device ID\".",
      "Type that Device ID and your Square Access Token (also found on the same developer page) into the two boxes below."
    ],
    tip: "This pairing step only needs to be done once per device. If your Square account was set up by someone else, ask them for the Access Token — it's a long code, safest to copy-paste rather than re-type."
  },
  ADYEN: {
    name: "Adyen",
    logoUrl: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTLwbTNAl15w8i2X2hvS1FQlVFTRuJbEAoi_m2aUJwGJ1RhL18li_Po4fQ&s=10",
    connectionTypes: [
      { value: "LOCAL_LAN", label: "Same Wi-Fi as this computer (recommended)" },
      { value: "CLOUD", label: "Internet / Cloud Mode" }
    ],
    models: [
      { value: "ADYEN_NORTH_AMERICA", label: "North America Smart Terminal" },
      { value: "ADYEN_GENERIC", label: "Adyen Generic Reader" }
    ],
    defaultPort: "8443",
    steps: [
      "On a computer, log in to your Adyen Customer Area (the website Adyen gave you when you signed up).",
      "Click Point of Sale, then Terminals, and find your card machine in the list.",
      "You'll see either an IP Address (4 numbers separated by dots, like 192.168.1.60) or a Cloud ID next to it — write down whichever one is shown.",
      "Type that number into the \"Machine IP Address\" box below. If your internet router supports it, ask your IT person to \"reserve\" this IP for the terminal so it never changes."
    ],
    tip: "Adyen's dashboard can look busy at first glance — you only need the one number next to your terminal's name, nothing else on that page matters for this step."
  },
  PINE_LABS: {
    name: "Pine Labs (India)",
    logoUrl: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQSWMtmHPJkKFRxPBaCiw49vdQNLgBaxtG9Ls2hwYr2LY2RXP0KkMsG9WcJ&s=10",
    connectionTypes: [
      { value: "CLOUD", label: "Internet / Cloud Mode" }
    ],
    models: [
      { value: "PINE_LABS_POS", label: "Pine Labs POS Terminal" }
    ],
    defaultPort: "",
    steps: [
      "On the Pine Labs machine, go into its Settings and turn on \"Cloud ECR Integration\" (ECR just means it can talk to your billing software).",
      "Call or WhatsApp Pine Labs support and ask them for your Merchant ID, Terminal ID, and Server URL — they can send these to you directly.",
      "Type the Terminal ID and the token/key Pine Labs gave you into the boxes below."
    ],
    tip: "Pine Labs support is usually the fastest way to get these three details — you don't have to dig through any settings menu yourself if you'd rather just call them."
  },
  PAYTM: {
    name: "Paytm (India)",
    logoUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/2/24/Paytm_Logo_%28standalone%29.svg/1280px-Paytm_Logo_%28standalone%29.svg.png?utm_source=commons.wikimedia.org&utm_campaign=index&utm_content=thumbnail",
    connectionTypes: [
      { value: "CLOUD", label: "Internet / Cloud Mode" }
    ],
    models: [
      { value: "PAYTM_POS", label: "Paytm POS Terminal" }
    ],
    defaultPort: "",
    steps: [
      "On a computer, log in to your Paytm Merchant Dashboard (the same login you use to check your Paytm payments).",
      "Click Developers, then ECR Integrations. You'll see your Paytm MID (Merchant ID) and a Secret Key on this page.",
      "Type the MID and Secret Key into the boxes below, along with the serial number printed on your Paytm device."
    ],
    tip: "MID and Secret Key are both just short codes shown on your dashboard — copy them exactly as written, including any dashes or capital letters."
  },
  VERIFONE: {
    name: "Verifone",
    logoUrl: "https://commons.wikimedia.org/wiki/Special:FilePath/Verifone_Logo.svg",
    connectionTypes: [
      { value: "CLOUD", label: "Internet / Cloud Mode" }
    ],
    models: [
      { value: "VERIFONE_CARBON", label: "Verifone Carbon" },
      { value: "VERIFONE_E355", label: "Verifone e355" },
      { value: "VERIFONE_P400", label: "Verifone P400" }
    ],
    defaultPort: "",
    steps: [
      "Call or email whoever set up your card payments (your bank, or a payment company) and ask: \"Do I have Verifone's own Terminal API, or is Verifone connected through another payment gateway?\" They'll know the answer.",
      "Ask them for the Terminal ID (or Serial Number) and an API Key/Secret for that connection — these are usually emailed to merchants when the terminal is first set up.",
      "Type the Terminal ID and API Key/Secret into the boxes below. Note: this is a new connection type we're still finishing — \"Test Link\" will confirm it saved correctly, but a real card swipe isn't wired up yet."
    ],
    tip: "Don't worry about the technical wording here — just forward whatever email or message your payment provider sent you when they gave you the terminal, and we'll figure out what fields those match."
  }
};

const PaymentTerminalSettings = () => {
  const { Get, Post, Patch, Delete, getAPI } = useApi();
  const { user } = useContext(AuthContext);
  const storeId = user?.store;

  const [terminals, setTerminals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [showGuide, setShowGuide] = useState(false);

  // Form State
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState("");
  const [provider, setProvider] = useState("CLOVER");
  const [model, setModel] = useState("FLEX_4");
  const [connectionType, setConnectionType] = useState("LOCAL_LAN");
  const [machineIp, setMachineIp] = useState("");
  const [port, setPort] = useState("12345");
  const [terminalId, setTerminalId] = useState("");
  const [deviceId, setDeviceId] = useState("");
  const [posId, setPosId] = useState("");
  const [secretKey, setSecretKey] = useState("");

  // Ad-hoc "raw socket" connection tester - lets someone paste the exact
  // wss://<ip>:12345/remote_pay URI and serial shown on the terminal's own
  // Network Pay Display screen and try pairing/a test sale directly,
  // without first saving a full terminal record. Useful for confirming a
  // terminal actually works before wiring it into a saved config.
  const SOCKET_TEST_STORAGE_KEY = "paisavara_clover_socket_test_config";
  const loadSocketTestConfig = () => {
    try {
      const saved = JSON.parse(localStorage.getItem(SOCKET_TEST_STORAGE_KEY) || "{}");
      return {
        uri: saved.uri || "",
        serial: saved.serial || "",
        posName: saved.posName || "Paisavara POS",
        appId: saved.appId || "com.paisavara.pos:1.0.0",
        authToken: saved.authToken || "",
      };
    } catch {
      return { uri: "", serial: "", posName: "Paisavara POS", appId: "com.paisavara.pos:1.0.0", authToken: "" };
    }
  };
  const initialSocketTestConfig = loadSocketTestConfig();

  const [showSocketTest, setShowSocketTest] = useState(false);
  const [socketTestUri, setSocketTestUri] = useState(initialSocketTestConfig.uri);
  const [socketTestSerial, setSocketTestSerial] = useState(initialSocketTestConfig.serial);
  const [socketTestPosName, setSocketTestPosName] = useState(initialSocketTestConfig.posName);
  const [socketTestAppId, setSocketTestAppId] = useState(initialSocketTestConfig.appId);
  const [socketTesting, setSocketTesting] = useState(false);
  const [socketTestLog, setSocketTestLog] = useState([]);
  const [socketTestAuthToken, setSocketTestAuthToken] = useState(initialSocketTestConfig.authToken);

  // Remember whatever's typed into the socket tester (and any pairing token
  // earned) in localStorage, so it's still there next time - no re-entering
  // the same URL/serial on every visit. Just change whichever field needs it.
  useEffect(() => {
    try {
      localStorage.setItem(
        SOCKET_TEST_STORAGE_KEY,
        JSON.stringify({
          uri: socketTestUri,
          serial: socketTestSerial,
          posName: socketTestPosName,
          appId: socketTestAppId,
          authToken: socketTestAuthToken,
        })
      );
    } catch {
      // localStorage unavailable (private mode, etc.) - not critical, just skip persisting.
    }
  }, [socketTestUri, socketTestSerial, socketTestPosName, socketTestAppId, socketTestAuthToken]);

  const fetchTerminals = async () => {
    if (!storeId) return;
    setLoading(true);
    try {
      const data = await Get(`${getAPI('paymentTerminals')}?store_id=${storeId}`);
      setTerminals(data || []);
    } catch (error) {
      console.error("Error fetching terminals:", error);
      toast.error("Failed to load payment terminals.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTerminals();
  }, [storeId]);

  const handleProviderChange = (newProvider) => {
    setProvider(newProvider);
    const config = PROVIDER_CONFIG[newProvider];
    const firstConn = config.connectionTypes[0].value;
    setConnectionType(firstConn);
    setModel(config.models[0].value);
    setPort(config.defaultPort || "");
    setShowGuide(false);
  };

  const resetForm = () => {
    setEditingId(null);
    setName("");
    setProvider("CLOVER");
    setModel("FLEX_4");
    setConnectionType("LOCAL_LAN");
    setMachineIp("");
    setPort("12345");
    setTerminalId("");
    setDeviceId("");
    setPosId("");
    setSecretKey("");
    setShowForm(false);
    setShowGuide(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Terminal name is required.");
      return;
    }
    if (provider === "CLOVER" && connectionType === "LOCAL_LAN" && (!deviceId.trim() || !posId.trim())) {
      toast.error("Device Serial/ID and POS ID Reference are required for Clover Local LAN terminals (sent as X-Clover-Device-Id / X-POS-Id on every request to the device).");
      return;
    }

    const payload = {
      store_id: storeId,
      name,
      provider,
      model,
      connection_type: connectionType,
      machine_ip: connectionType === "LOCAL_LAN" ? machineIp : null,
      port: connectionType === "LOCAL_LAN" ? Number(port) : null,
      terminal_id: connectionType === "CLOUD" ? terminalId : null,
      device_id: deviceId || null,
      pos_id: posId || null,
      secret_key: connectionType === "CLOUD" ? secretKey : null,
    };

    try {
      if (editingId) {
        await Patch("paymentTerminals", editingId, payload);
        toast.success("Terminal updated successfully!");
      } else {
        await Post("paymentTerminals", payload);
        toast.success("Terminal registered successfully!");
      }
      resetForm();
      fetchTerminals();
    } catch (error) {
      console.error("Error saving terminal:", error);
      toast.error(error.response?.data?.detail || "Failed to save terminal settings.");
    }
  };

  const handleEdit = (t) => {
    setEditingId(t.id);
    setName(t.name);
    setProvider(t.provider);
    setModel(t.model);
    setConnectionType(t.connection_type);
    setMachineIp(t.machine_ip || "");
    setPort(String(t.port || ""));
    setTerminalId(t.terminal_id || "");
    setDeviceId(t.device_id || "");
    setPosId(t.pos_id || "");
    setSecretKey("");
    setShowForm(true);
    setShowGuide(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to unregister this payment terminal?")) return;
    try {
      const response = await Delete("paymentTerminals", id);
      if (response?.data?.soft_deleted) {
        // Terminal has real transaction history (test sales count too), so the
        // backend disabled it instead of a hard delete - still disappears from
        // the active list, transaction history stays intact.
        toast.info(response.data.detail || "Terminal has transaction history, so it was disabled instead of deleted.");
      } else {
        toast.success("Terminal unregistered.");
      }
      fetchTerminals();
    } catch (error) {
      console.error("Error deleting terminal:", error);
      toast.error("Failed to delete terminal.");
    }
  };

  const describeLocalTestError = (err, t) => {
    const raw = err?.message || String(err);
    if (err?.name === "TypeError" || /Failed to fetch|NetworkError|Load failed/i.test(raw)) {
      return (
        `Could not reach ${t.name} at ${t.machine_ip}:${t.port || 12345}. This usually means one of: ` +
        `(1) this computer and the terminal aren't on the same Wi-Fi/LAN, ` +
        `(2) the IP address or port in Settings is wrong or the terminal is asleep/off, or ` +
        `(3) the terminal's security certificate isn't trusted yet in this browser — open ` +
        `https://${t.machine_ip}:${t.port || 12345}/ in a new tab once and accept the certificate warning, then try again.`
      );
    }
    return raw;
  };

  const appendSocketLog = (msg) => {
    const time = new Date().toLocaleTimeString();
    setSocketTestLog((prev) => [...prev, `[${time}] ${msg}`]);
  };

  const handleSocketConnectionTest = async () => {
    if (!socketTestUri.trim()) {
      toast.error("Paste the wss://... URI shown on the terminal's Network Pay Display screen first.");
      return;
    }
    if (!socketTestSerial.trim()) {
      toast.error("Enter the terminal's Serial Number (shown above the URI on the terminal screen).");
      return;
    }
    setSocketTesting(true);
    setSocketTestLog([]);
    appendSocketLog(`Connecting to ${socketTestUri.trim()} ...`);
    try {
      const response = await runCloverLocalSale({
        uri: socketTestUri.trim(),
        applicationId: socketTestAppId.trim() || "com.paisavara.pos:1.0.0",
        posName: socketTestPosName.trim() || "Paisavara POS",
        serialNumber: socketTestSerial.trim(),
        authToken: socketTestAuthToken || undefined,
        amountCents: 200,
        externalId: `SOCKET-TEST-${Date.now()}`,
        onPairingCode: (code) => {
          appendSocketLog(`Pairing code: ${code} — confirm this matches the terminal's screen.`);
          toast.info(`Pairing code: ${code}`, { autoClose: 15000 });
        },
        onAuthToken: (newAuthToken) => {
          setSocketTestAuthToken(newAuthToken);
          appendSocketLog("Pairing succeeded.");
          // If this Serial Number matches an already-saved Clover terminal,
          // push the pairing token (and the IP/port actually used here, in
          // case Settings still has a stale one) straight to that terminal
          // record. That's what the LIVE checkout flow (PosData.js) reads -
          // without this, a successful pairing here wouldn't help real
          // sales, which would just have to pair all over again.
          const matched = terminals.find(
            (t) => t.provider === "CLOVER" && t.device_id && t.device_id.trim() === socketTestSerial.trim()
          );
          if (matched) {
            const uriMatch = socketTestUri.trim().match(/^wss?:\/\/([^:/]+)(?::(\d+))?/i);
            const patchPayload = { clover_ws_auth_token: newAuthToken };
            if (uriMatch) {
              if (uriMatch[1] && uriMatch[1] !== matched.machine_ip) patchPayload.machine_ip = uriMatch[1];
              if (uriMatch[2] && Number(uriMatch[2]) !== matched.port) patchPayload.port = Number(uriMatch[2]);
            }
            Patch("paymentTerminals", matched.id, patchPayload)
              .then(() => {
                appendSocketLog(`Saved pairing token to terminal "${matched.name}" — live checkout will use it automatically, no re-pairing needed.`);
                fetchTerminals();
              })
              .catch(() => {
                appendSocketLog(`Pairing succeeded, but couldn't save the token to terminal "${matched.name}" automatically — it'll still work here, but live checkout may ask to re-pair.`);
              });
          } else {
            appendSocketLog("No saved terminal matches this Serial Number, so this token is only remembered here (this test panel), not for live checkout. Save a terminal with this Serial Number in Settings to use it for real sales.");
          }
        },
        onStatus: (msg) => appendSocketLog(msg),
      });
      appendSocketLog("Sale reported success — check the terminal screen to confirm the $2.00 test charge appeared.");
      toast.success("Socket test succeeded — terminal accepted the connection and the sale.");
    } catch (err) {
      const raw = err?.message || String(err);
      appendSocketLog(`FAILED: ${raw}`);
      toast.error(`Socket test failed: ${raw}`);
    } finally {
      setSocketTesting(false);
    }
  };

  const handleTestConnection = async (t) => {
    setTestingId(t.id);

    if (t.connection_type === "LOCAL_LAN") {
      if (t.provider === "CLOVER" && (!t.device_id || !t.pos_id)) {
        toast.error("Add this terminal's Device Serial/ID and POS ID Reference first (Clover requires both for local requests) — click Edit.");
        setTestingId(null);
        return;
      }

      toast.info(`Sending a test intent to ${t.name}...`);
      let paymentId = null;
      try {
        const testRes = await Post(`${getAPI('paymentTerminals')}${t.id}/test/`, {});
        const data = testRes.data;
        paymentId = data.payment_id;
        const dispatch = data.dispatch_result?.dispatch;

        if (dispatch && dispatch.required && dispatch.type === "CLOVER_WS") {
          // This specific terminal (Clover Flex/Mini/C-series over local Wi-Fi)
          // speaks the CloverConnector WebSocket protocol ("Secure Network Pay
          // Display"), not REST — the browser (on the same LAN as the device)
          // pairs (first time only) and sends the sale directly via Clover's
          // official SDK.
          const response = await runCloverLocalSale({
            uri: dispatch.uri,
            applicationId: dispatch.applicationId,
            posName: dispatch.posName,
            serialNumber: dispatch.serialNumber,
            authToken: dispatch.authToken,
            amountCents: dispatch.amount,
            externalId: dispatch.externalId,
            onPairingCode: (code) => {
              toast.info(`Pairing code from terminal: ${code}. Confirm this matches what's shown on ${t.name}'s screen.`, { autoClose: 15000 });
            },
            onAuthToken: (newAuthToken) => {
              // Persist so future tests/checkouts on this terminal skip pairing.
              Patch("paymentTerminals", t.id, { clover_ws_auth_token: newAuthToken }).catch(() => {});
            },
            onStatus: (msg) => toast.info(msg, { autoClose: 3000 }),
          });
          await Post(`${getAPI('paymentsTerminal')}${paymentId}/report/`, {
            status: "WAITING_FOR_CUSTOMER",
            provider_transaction_id: response?.getPayment?.()?.getId?.() || undefined,
          }).catch(() => {});
          toast.success(
            `Connected! A test charge was sent to ${t.name} over Wi-Fi and it reported success. Status: ONLINE.`
          );
        } else if (dispatch && dispatch.required) {
          // Legacy REST-based local dispatch (other providers/models).
          const res = await fetch(dispatch.url, {
            method: dispatch.method || "POST",
            headers: dispatch.headers || { "Content-Type": "application/json" },
            body: JSON.stringify(dispatch.payload),
          });
          if (!res.ok) {
            const bodyText = await res.text().catch(() => "");
            throw new Error(`Terminal responded with HTTP ${res.status}${bodyText ? `: ${bodyText}` : ""}`);
          }
          const resData = await res.json().catch(() => ({}));
          await Post(`${getAPI('paymentsTerminal')}${paymentId}/report/`, {
            status: "WAITING_FOR_CUSTOMER",
            provider_transaction_id: resData.id || resData.paymentId,
          }).catch(() => {});
          toast.success(
            `Connected! A test charge is now showing on ${t.name}'s screen — you can cancel it there, no need to swipe for this check. Status: ONLINE.`
          );
        } else if (data.dispatch_result?.status === "FAILED") {
          throw new Error(data.dispatch_result.error || "Terminal rejected the test request.");
        } else {
          // Mock terminal or a cloud provider the backend could resolve directly.
          toast.success(`Local LAN test: ${data.status}`);
        }
        fetchTerminals();
      } catch (err) {
        console.error("Local terminal test failed:", err);
        const message = describeLocalTestError(err, t);
        toast.error(message);
        if (paymentId) {
          Post(`${getAPI('paymentsTerminal')}${paymentId}/report/`, {
            status: "FAILED",
            failure_message: message,
          }).catch(() => {});
        }
        fetchTerminals();
      } finally {
        setTestingId(null);
      }
    } else {
      // CLOUD integration - call backend cloud endpoints
      try {
        const response = await Post(`${getAPI('paymentTerminals')}${t.id}/test/`, {});
        if (response.data.status === "ONLINE") {
          toast.success("Cloud link test: ONLINE (Test transaction sent to reader!)");
        } else {
          toast.error("Cloud link test: OFFLINE (Device unreachable)");
        }
        fetchTerminals();
      } catch (error) {
        console.error("Error testing connection:", error);
        toast.error("Connection test failed.");
      } finally {
        setTestingId(null);
      }
    }
  };

  return (
    <div className="responsive-box store-config-panel">
      <div className="store-config-header">
        <div>
          <span className="store-config-kicker">Device Integration</span>
          <h2>Payment Terminals</h2>
          <p>Register and manage active card-present checkouts and network connections.</p>
        </div>
        {!showForm && (
          <Can module="paymentTerminalService" action="add">
            <button
              type="button"
              className="store-config-save"
              style={{ minWidth: "150px" }}
              onClick={() => setShowForm(true)}
            >
              + Add Terminal
            </button>
          </Can>
        )}
      </div>

      <div style={{ background: '#fefce8', border: '1px solid #fde68a', borderRadius: '16px', padding: '20px 22px', marginBottom: '24px' }}>
        <div
          role="button"
          tabIndex={0}
          aria-expanded={showSocketTest}
          onClick={() => setShowSocketTest((v) => !v)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowSocketTest((v) => !v); } }}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }}
        >
          <div>
            <h4 style={{ margin: 0, color: '#92400e', fontSize: '15px', fontWeight: 850 }}>Test a Wi-Fi (WebSocket) terminal connection directly</h4>
            <p style={{ margin: '2px 0 0', color: '#a16207', fontSize: '13px' }}>
              {showSocketTest ? 'Click to hide.' : "Paste the wss://... address and Serial Number shown on the terminal's own screen (e.g. Network Pay Display) to test pairing and a $2.00 sale, without saving a terminal first."}
            </p>
          </div>
          <span aria-hidden="true" style={{ fontSize: '14px', color: '#b45309', transform: showSocketTest ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.18s ease' }}>▾</span>
        </div>

        {showSocketTest && (
          <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#78350f', marginBottom: '4px' }}>Terminal Socket URL *</label>
                <input
                  type="text"
                  placeholder="wss://192.168.2.231:12345/remote_pay"
                  value={socketTestUri}
                  onChange={(e) => setSocketTestUri(e.target.value)}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: '10px', border: '1px solid #fde68a' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#78350f', marginBottom: '4px' }}>Serial Number *</label>
                <input
                  type="text"
                  placeholder="C046UG54930779"
                  value={socketTestSerial}
                  onChange={(e) => setSocketTestSerial(e.target.value)}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: '10px', border: '1px solid #fde68a' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#78350f', marginBottom: '4px' }}>POS Name (optional)</label>
                <input
                  type="text"
                  placeholder="Paisavara POS"
                  value={socketTestPosName}
                  onChange={(e) => setSocketTestPosName(e.target.value)}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: '10px', border: '1px solid #fde68a' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#78350f', marginBottom: '4px' }}>Application ID (optional)</label>
                <input
                  type="text"
                  placeholder="com.paisavara.pos:1.0.0"
                  value={socketTestAppId}
                  onChange={(e) => setSocketTestAppId(e.target.value)}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: '10px', border: '1px solid #fde68a' }}
                />
              </div>
            </div>

            <div>
              <button
                type="button"
                onClick={handleSocketConnectionTest}
                disabled={socketTesting}
                className="store-config-save"
                style={{ minWidth: '160px', background: socketTesting ? '#94a3b8' : '#b45309', boxShadow: 'none' }}
              >
                {socketTesting ? "Connecting..." : "Test Connection ($2.00)"}
              </button>
            </div>

            {socketTestLog.length > 0 && (
              <div style={{ background: '#1e293b', color: '#e2e8f0', borderRadius: '10px', padding: '12px 14px', fontFamily: 'monospace', fontSize: '12px', maxHeight: '180px', overflowY: 'auto' }}>
                {socketTestLog.map((line, i) => (
                  <div key={i}>{line}</div>
                ))}
              </div>
            )}

            <p style={{ margin: 0, color: '#a16207', fontSize: '12px' }}>
              On the terminal: open "Network Pay Display" (or whichever local pay-display app it has), tap "Start", then "Initiate Pairing" — the URL and Serial Number shown there are exactly what to paste above. First run will show a pairing code here to confirm on the terminal.
            </p>
          </div>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleSave} style={{ marginBottom: "30px" }}>
          <div className="store-config-header">
            <h3>{editingId ? "Edit Terminal" : "Register Payment Terminal"}</h3>
          </div>

          {/* Stepper setup guide for the currently selected provider — collapsed by default, expands on click */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', background: '#eff6ff', padding: '22px', borderRadius: '16px', border: '1px solid #bfdbfe', marginBottom: '24px', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
            <div
              role="button"
              tabIndex={0}
              aria-expanded={showGuide}
              onClick={() => setShowGuide((v) => !v)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowGuide((v) => !v); } }}
              style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', cursor: 'pointer', userSelect: 'none' }}
            >
              <img src={PROVIDER_CONFIG[provider].logoUrl} alt={provider} style={{ height: '32px', maxWidth: '100px', objectFit: 'contain', flexShrink: 0 }} />
              <div style={{ minWidth: 0, flex: '1 1 auto' }}>
                <h4 style={{ margin: 0, color: '#1e3a8a', fontSize: '16px', fontWeight: '850' }}>How to connect your {PROVIDER_CONFIG[provider].name} machine</h4>
                <p style={{ margin: '2px 0 0', color: '#3b5aa6', fontSize: '13px' }}>{showGuide ? 'Click to hide the steps.' : 'Click here to see the step-by-step setup guide.'}</p>
              </div>
              <span
                aria-hidden="true"
                style={{
                  fontSize: '14px',
                  color: '#2563eb',
                  flexShrink: 0,
                  transform: showGuide ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.18s ease',
                  display: 'inline-flex',
                }}
              >
                ▾
              </span>
            </div>
            {showGuide && (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '14px', width: '100%' }}>
                  {PROVIDER_CONFIG[provider].steps.map((step, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', width: '100%' }}>
                      <span style={{ display: 'inline-flex', width: '26px', height: '26px', borderRadius: '50%', background: '#2563eb', color: '#ffffff', fontSize: '13px', fontWeight: 'bold', justifyContent: 'center', alignItems: 'center', flexShrink: 0 }}>
                        {idx + 1}
                      </span>
                      <span style={{ flex: '1 1 auto', minWidth: 0, fontSize: '15px', color: '#1e3a5f', lineHeight: '1.6', fontWeight: '500', overflowWrap: 'break-word', wordBreak: 'break-word', whiteSpace: 'normal' }}>{step}</span>
                    </div>
                  ))}
                </div>
                {PROVIDER_CONFIG[provider].tip && (
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginTop: '16px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '12px', padding: '14px 16px', width: '100%', boxSizing: 'border-box' }}>
                    <span style={{ fontSize: '18px', lineHeight: 1, flexShrink: 0 }}>💡</span>
                    <span style={{ flex: '1 1 auto', minWidth: 0, fontSize: '14px', color: '#78350f', lineHeight: '1.6', overflowWrap: 'break-word', wordBreak: 'break-word', whiteSpace: 'normal' }}>{PROVIDER_CONFIG[provider].tip}</span>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="store-config-grid">
            <div className="store-config-field">
              <div>
                <label htmlFor="term-name">Give it a name *</label>
                <span>Anything that helps you recognize it later, like "Front Counter" or "Till 2".</span>
              </div>
              <input
                id="term-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Front Counter"
                required
              />
            </div>

            <div className="store-config-field">
              <div>
                <label htmlFor="term-provider">Which brand is your card machine?</label>
                <span>Pick the company whose logo/name is on your physical device.</span>
              </div>
              <select
                id="term-provider"
                value={provider}
                onChange={(e) => handleProviderChange(e.target.value)}
              >
                {Object.keys(PROVIDER_CONFIG).map((key) => (
                  <option key={key} value={key}>
                    {PROVIDER_CONFIG[key].name}
                  </option>
                ))}
              </select>
            </div>

            <div className="store-config-field">
              <div>
                <label htmlFor="term-connection">How does it connect?</label>
                <span>Most shops use the same Wi-Fi option — pick that unless your provider told you otherwise.</span>
              </div>
              <select
                id="term-connection"
                value={connectionType}
                onChange={(e) => setConnectionType(e.target.value)}
              >
                {PROVIDER_CONFIG[provider].connectionTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="store-config-field">
              <div>
                <label htmlFor="term-model">Which model do you have?</label>
                <span>Check the model name printed on the front or back of the device.</span>
              </div>
              <select
                id="term-model"
                value={model}
                onChange={(e) => setModel(e.target.value)}
              >
                {PROVIDER_CONFIG[provider].models.map((mod) => (
                  <option key={mod.value} value={mod.value}>
                    {mod.label}
                  </option>
                ))}
              </select>
            </div>

            {connectionType === "LOCAL_LAN" && (
              <>
                <div className="store-config-field">
                  <div>
                    <label htmlFor="term-ip">Machine's Wi-Fi number (IP Address) *</label>
                    <span>4 numbers separated by dots, found in the device's Settings/Network menu — see Step {PROVIDER_CONFIG[provider].steps.length >= 3 ? 3 : 2} above.</span>
                  </div>
                  <input
                    id="term-ip"
                    type="text"
                    value={machineIp}
                    onChange={(e) => setMachineIp(e.target.value)}
                    placeholder="e.g. 192.168.1.50"
                    required
                  />
                </div>

                <div className="store-config-field">
                  <div>
                    <label htmlFor="term-port">Port (leave as-is)</label>
                    <span>Almost always fine to leave this at the default — {PROVIDER_CONFIG[provider].defaultPort || 12345}.</span>
                  </div>
                  <input
                    id="term-port"
                    type="number"
                    value={port}
                    onChange={(e) => setPort(e.target.value)}
                    placeholder={PROVIDER_CONFIG[provider].defaultPort || "12345"}
                  />
                </div>
              </>
            )}

            {connectionType === "CLOUD" && (
              <>
                <div className="store-config-field">
                  <div>
                    <label htmlFor="term-reader">Reader / Terminal ID *</label>
                    <span>A short code your provider's website shows next to your device — see the steps above for exactly where.</span>
                  </div>
                  <input
                    id="term-reader"
                    type="text"
                    value={terminalId}
                    onChange={(e) => setTerminalId(e.target.value)}
                    placeholder="e.g. tmr_12345"
                    required
                  />
                </div>

                <div className="store-config-field">
                  <div>
                    <label htmlFor="term-secret">API Key / Secret Token</label>
                    <span>A private password-like code from your provider's dashboard — keep it just as secret as a password.</span>
                  </div>
                  <input
                    id="term-secret"
                    type="password"
                    value={secretKey}
                    onChange={(e) => setSecretKey(e.target.value)}
                    placeholder="••••••••••••••••"
                  />
                </div>
              </>
            )}

            <div className="store-config-field">
              <div>
                <label htmlFor="term-serial">
                  Device Serial Number{provider === "CLOVER" && connectionType === "LOCAL_LAN" ? " *" : ""}
                </label>
                <span>
                  {provider === "CLOVER" && connectionType === "LOCAL_LAN"
                    ? "Required for Clover on Wi-Fi. Look on a sticker on the back of the device, or under Settings > About on the device screen."
                    : "Optional — the serial number printed on the device, if you have it handy."}
                </span>
              </div>
              <input
                id="term-serial"
                type="text"
                value={deviceId}
                onChange={(e) => setDeviceId(e.target.value)}
                placeholder="e.g. C043UX12345"
                required={provider === "CLOVER" && connectionType === "LOCAL_LAN"}
              />
            </div>

            <div className="store-config-field">
              <div>
                <label htmlFor="term-pos">
                  Till / Register Name{provider === "CLOVER" && connectionType === "LOCAL_LAN" ? " *" : ""}
                </label>
                <span>
                  {provider === "CLOVER" && connectionType === "LOCAL_LAN"
                    ? "Required for Clover on Wi-Fi. Just make up any short name for this counter, like register-1."
                    : "Optional — a short name for this counter, like register-1, to help you tell tills apart."}
                </span>
              </div>
              <input
                id="term-pos"
                type="text"
                value={posId}
                onChange={(e) => setPosId(e.target.value)}
                placeholder="e.g. register-1"
                required={provider === "CLOVER" && connectionType === "LOCAL_LAN"}
              />
            </div>
          </div>

          <div className="store-config-actions" style={{ gap: "12px", justifyContent: "flex-start", marginTop: "24px" }}>
            <button type="submit" className="store-config-save">
              Save Terminal
            </button>
            <button
              type="button"
              className="store-config-reset"
              onClick={resetForm}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p style={{ color: "#64748b", fontSize: "15px" }}>Loading terminals...</p>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: "20px", marginTop: "20px" }}>
          {terminals.map((t) => (
            <div
              key={t.id}
              className="store-config-field"
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                gap: "14px",
                padding: "20px",
                height: "100%",
                justifyContent: "space-between"
              }}
            >
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                  <img src={PROVIDER_CONFIG[t.provider]?.logoUrl} alt={t.provider} style={{ height: '24px', maxWidth: '80px', objectFit: 'contain' }} />
                  <span
                    style={{
                      fontSize: "12px",
                      background: t.last_health_status === "ONLINE" ? "#eefbf2" : "#fdf2f2",
                      color: t.last_health_status === "ONLINE" ? "#1f8a4c" : "#9b1c1c",
                      padding: "4px 10px",
                      borderRadius: "999px",
                      fontWeight: "800",
                    }}
                  >
                    {t.last_health_status}
                  </span>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  <h4 style={{ margin: 0, fontSize: "16px", fontWeight: "850", color: "#0f172a" }}>{t.name}</h4>
                  <span style={{ fontSize: "13px", color: "#64748b" }}>
                    <strong>Model:</strong> {t.model || "Generic"}
                  </span>
                  <span style={{ fontSize: "13px", color: "#64748b" }}>
                    <strong>Connection:</strong> {t.connection_type === "LOCAL_LAN" ? `Local IP (${t.machine_ip}:${t.port || 12345})` : `Cloud Reader (${t.terminal_id || "Registered"})`}
                  </span>
                </div>
              </div>

              <div style={{ display: "flex", gap: "10px", marginTop: "16px" }}>
                <button
                  onClick={() => handleTestConnection(t)}
                  disabled={testingId === t.id}
                  className="store-config-save"
                  style={{
                    flex: 1,
                    minWidth: "auto",
                    padding: "8px 12px",
                    fontSize: "13px",
                    background: testingId === t.id ? "#94a3b8" : "#2563eb",
                    boxShadow: "none"
                  }}
                >
                  {testingId === t.id ? "Testing..." : "Test Link"}
                </button>
                <Can module="paymentTerminalService" action="change">
                  <button
                    onClick={() => handleEdit(t)}
                    className="store-config-reset"
                    style={{
                      minWidth: "auto",
                      padding: "8px 16px",
                      fontSize: "13px",
                      boxShadow: "none"
                    }}
                  >
                    Edit
                  </button>
                </Can>
                <Can module="paymentTerminalService" action="delete">
                  <button
                    onClick={() => handleDelete(t.id)}
                    className="store-config-reset"
                    style={{
                      minWidth: "auto",
                      padding: "8px 16px",
                      fontSize: "13px",
                      background: "#fef2f2",
                      color: "#b91c1c",
                      boxShadow: "none"
                    }}
                  >
                    Delete
                  </button>
                </Can>
              </div>
            </div>
          ))}

          {terminals.length === 0 && !showForm && (
            <div
              style={{
                gridColumn: "1/-1",
                textAlign: "center",
                color: "#64748b",
                padding: "40px 0",
                fontSize: "15px"
              }}
            >
              No payment terminals configured. Click "+ Add Terminal" to link your device.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PaymentTerminalSettings;
